/**
 * useScrollPosition Hook
 *
 * Custom hook to detect when the filter control bar scrolls out of view
 * and trigger the display of the sticky audit session control bar.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface UseScrollPositionOptions {
  /** CSS selector or ref for the element to track */
  targetSelector?: string;
  /** Offset from the target element's position (in pixels) */
  offset?: number;
  /** Throttle delay for scroll events (in milliseconds) */
  throttleMs?: number;
}

interface ScrollPositionState {
  /** Whether the target element has scrolled out of view */
  isTargetOutOfView: boolean;
  /** Current scroll position from top of document */
  scrollY: number;
  /** Whether the user is currently scrolling */
  isScrolling: boolean;
}

/**
 * Hook to track scroll position and determine when a target element is out of view
 */
export function useScrollPosition(options: UseScrollPositionOptions = {}): ScrollPositionState {
  const {
    targetSelector = '[data-audit-filters-container]',
    offset = 0,
    throttleMs = 16, // ~60fps
  } = options;

  const [state, setState] = useState<ScrollPositionState>({
    isTargetOutOfView: false,
    scrollY: 0,
    isScrolling: false,
  });

  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScrollTimeRef = useRef<number>(0);

  const updateScrollPosition = useCallback(() => {
    const now = Date.now();

    // Throttle scroll events
    if (now - lastScrollTimeRef.current < throttleMs) {
      return;
    }

    lastScrollTimeRef.current = now;

    const scrollY = window.scrollY;
    const targetElement = document.querySelector(targetSelector);

    let isTargetOutOfView = false;

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const targetBottom = rect.bottom + offset;

      // Target is out of view when its bottom edge is above the viewport
      isTargetOutOfView = targetBottom <= 0;
    }

    setState(prevState => {
      // Only update state if the visibility state has actually changed
      // Don't update for isScrolling changes to prevent re-renders during scroll
      if (prevState.isTargetOutOfView === isTargetOutOfView) {
        return prevState; // Return same object reference to prevent re-render
      }

      return {
        ...prevState,
        scrollY,
        isTargetOutOfView,
        isScrolling: true,
      };
    });

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Set scrolling to false after scroll stops
    scrollTimeoutRef.current = setTimeout(() => {
      setState(prevState => {
        // Only update if isScrolling is currently true
        if (!prevState.isScrolling) {
          return prevState; // Return same object reference to prevent re-render
        }

        return {
          ...prevState,
          isScrolling: false,
        };
      });
    }, 150);
  }, [targetSelector, offset, throttleMs]);

  useEffect(() => {
    // Initial position check
    updateScrollPosition();

    // Add scroll event listener
    window.addEventListener('scroll', updateScrollPosition, { passive: true });
    window.addEventListener('resize', updateScrollPosition, { passive: true });

    return () => {
      window.removeEventListener('scroll', updateScrollPosition);
      window.removeEventListener('resize', updateScrollPosition);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [updateScrollPosition]);

  return state;
}

/**
 * Hook specifically for tracking the audit items header
 */
export function useAuditFiltersScrollPosition() {
  return useScrollPosition({
    targetSelector: '[data-audit-items-header]',
    offset: 10, // Small buffer to ensure smooth transition
  });
}
